import axios from 'axios'
import { useAuthStore } from '../store/authStore'

// Always use same-origin API base. Nginx proxies /api/v1 for both dev/prod.
const API_BASE_URL = `${window.location.origin}/api/v1`

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout()
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  register: (data: {
    name: string
    email: string
    password: string
  }) => api.post('/auth/register', data),
  
  checkEmail: (email: string) =>
    api.get(`/auth/check-email?email=${encodeURIComponent(email)}`),
  
  verifyEmail: (token: string) =>
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`),
  
  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', { email }),
  
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
  
  googleLogin: (token: string) =>
    api.post('/auth/google', { token }),
  
  facebookLogin: (token: string) =>
    api.post('/auth/facebook', { token }),
  
  refreshToken: () => api.post('/auth/refresh'),
  
  logout: () => api.post('/auth/logout'),
}

// User API
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data: any) => api.put('/users/profile', data),
  changePassword: (data: any) => api.put('/users/password', data),
}

// File Compare API
export const fileCompareAPI = {
  // Download templates
  downloadTemplate: (templateName: string) =>
    api.get(`/file-compare/templates/${encodeURIComponent(templateName)}`, {
      responseType: 'blob',
    }),
  
  // Upload files
  uploadITCFile: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const token = useAuthStore.getState().token
    
    return api.post(`/file-compare/upload/ITC`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      },
    })
  },
  
  uploadGSTR2BFile: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const token = useAuthStore.getState().token
    
    return api.post(`/file-compare/upload/GSTR2B`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      },
    })
  },
  
  // Compare files
  compareFiles: (data: { itcFile: string, gstr2bFile: string }) =>
    api.post('/file-compare/compare', data),
  // Job status
  getStatus: (jobId: string) =>
    api.get(`/file-compare/status/${encodeURIComponent(jobId)}`),
  
  // Download comparison report
  downloadReport: (jobId: string) =>
    api.get(`/file-compare/download/${jobId}`, {
      responseType: 'blob',
    }),
}

// Task Management API
export const taskAPI = {
  getTasks: (params?: any) => api.get('/tasks', { params }),
  getTask: (id: string) => api.get(`/tasks/${id}`),
  createTask: (data: any) => api.post('/tasks', data),
  updateTask: (id: string, data: any) => api.put(`/tasks/${id}`, data),
  deleteTask: (id: string) => api.delete(`/tasks/${id}`),
  assignTask: (id: string, userId: string) =>
    api.put(`/tasks/${id}/assign`, { userId }),
}

// Query Management API
export const queryAPI = {
  getQueries: (params?: any) => api.get('/queries', { params }),
  getQuery: (id: string) => api.get(`/queries/${id}`),
  createQuery: (data: any) => api.post('/queries', data),
  updateQuery: (id: string, data: any) => api.put(`/queries/${id}`, data),
  deleteQuery: (id: string) => api.delete(`/queries/${id}`),
}

// Subscription API
export const subscriptionAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  getCurrentPlan: () => api.get('/subscriptions/current'),
  upgradePlan: (planId: string) => api.post('/subscriptions/upgrade', { planId }),
}

// Contact API
export const contactAPI = {
  submitForm: (data: {
    name: string
    email: string
    phone?: string
    company?: string
    message: string
  }) => api.post('/contact/submit', data),
}

export default api 
